import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/authContext";
import { createMeeting, type Contact, api } from "@/lib/api";
import { parseEasternDateTime } from "@/lib/etTime";
import { format } from "date-fns";
import { Loader2, X, Plus, Search, Globe } from "lucide-react";

const TZ_LABELS: Record<string, string> = {
  "America/New_York":    "Eastern (ET)",
  "America/Chicago":     "Central (CT)",
  "America/Denver":      "Mountain (MT)",
  "America/Los_Angeles": "Pacific (PT)",
};

interface Props {
  accountId: string;
  accountName: string;
  contacts: Contact[];
  onClose: () => void;
}

const DURATIONS = [
  { label: "30 min", value: 30 },
  { label: "60 min", value: 60 },
  { label: "90 min", value: 90 },
  { label: "2 hours", value: 120 },
];

export function ScheduleMeetingModal({ accountId, accountName, contacts, onClose }: Props) {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const today = format(new Date(), "yyyy-MM-dd");
  const defaultTopic = `${accountName} — ${format(new Date(), "MMM d, yyyy")}`;

  const [topic, setTopic] = useState(defaultTopic);
  const [date, setDate] = useState(today);
  const [time, setTime] = useState("10:00");
  const [duration, setDuration] = useState(60);
  const [hostEmail, setHostEmail] = useState(user?.email ?? "");
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [altHostEmail, setAltHostEmail] = useState("");
  const [selectedAttendees, setSelectedAttendees] = useState<Set<string>>(new Set());
  const [staffSearch, setStaffSearch] = useState("");
  const [guestInput, setGuestInput] = useState("");
  const [guestEmails, setGuestEmails] = useState<string[]>([]);

  const { data: staffUsers = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.getUsers(),
    // Set default host to current user once users load (in case email wasn't ready on mount)
    select: (data) => {
      if (!hostEmail && user?.email) setHostEmail(user.email);
      return data;
    },
  });

  const { data: account } = useQuery({
    queryKey: ["account", accountId],
    queryFn: () => api.getAccount(accountId),
  });
  const clientTzLabel = account?.timezone ? (TZ_LABELS[account.timezone] ?? account.timezone) : null;

  // Exclude host and alt host from the "also attending" list
  const attendeePool = staffUsers.filter((u) => u.email !== hostEmail && u.email !== altHostEmail);
  const filteredAttendees = staffSearch.trim()
    ? attendeePool.filter((u) =>
        u.name.toLowerCase().includes(staffSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(staffSearch.toLowerCase()),
      )
    : attendeePool;

  const mutation = useMutation({
    mutationFn: () => {
      // The field is labeled "Time (ET)" — parse it as Eastern local time,
      // not the browser's local timezone (a staffer on Pacific typing
      // "2:00 PM" must schedule 2:00 PM ET, not 2:00 PM Pacific).
      const scheduledAt = parseEasternDateTime(date, time);
      const inviteeEmails = contacts
        .filter((c) => c.email && selectedContacts.has(c.id))
        .map((c) => c.email!);
      const staffAttendeeEmails = staffUsers
        .filter((u) => selectedAttendees.has(u.id) && u.email)
        .map((u) => u.email);
      return createMeeting(accountId, {
        topic,
        scheduled_at: scheduledAt,
        duration_minutes: duration,
        host_email: hostEmail || undefined,
        invitee_emails: inviteeEmails,
        alternative_host_emails: altHostEmail ? [altHostEmail] : [],
        staff_attendee_emails: staffAttendeeEmails,
        guest_emails: guestEmails,
      });
    },
    onSuccess: (meeting) => {
      queryClient.invalidateQueries({ queryKey: ["meetings", accountId] });
      const clientCount = contacts.filter((c) => c.email && selectedContacts.has(c.id)).length;
      const altHostCount = altHostEmail ? 1 : 0;
      const totalInvites = clientCount + altHostCount + selectedAttendees.size + guestEmails.length;
      toast({
        title: "Meeting scheduled",
        description: totalInvites > 0
          ? `Calendar invite sent to ${totalInvites} recipient${totalInvites > 1 ? "s" : ""}. Join link copied to clipboard.`
          : "Join link copied to clipboard.",
      });
      if (meeting.join_url) {
        navigator.clipboard.writeText(meeting.join_url).catch(() => {});
      }
      onClose();
    },
    onError: (err: Error) => {
      toast({ title: "Failed to schedule meeting", description: err.message, variant: "destructive" });
    },
  });

  const toggleContact = (contactId: string) => {
    setSelectedContacts((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId); else next.add(contactId);
      return next;
    });
  };

  const toggleAttendee = (userId: string) => {
    setSelectedAttendees((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const addGuest = () => {
    const email = guestInput.trim().toLowerCase();
    if (!email || !email.includes("@") || guestEmails.includes(email)) return;
    setGuestEmails((prev) => [...prev, email]);
    setGuestInput("");
  };

  const emailContacts = contacts.filter((c) => c.email);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Schedule Zoom Meeting</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label htmlFor="topic">Topic</Label>
            <Input
              id="topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Meeting topic"
            />
          </div>

          {/* Host picker — defaults to current user, switchable to any staff member */}
          <div className="space-y-1.5">
            <Label>Host</Label>
            <Select value={hostEmail} onValueChange={setHostEmail}>
              <SelectTrigger>
                <SelectValue placeholder="Select host…" />
              </SelectTrigger>
              <SelectContent>
                {staffUsers.map((u) => (
                  <SelectItem key={u.id} value={u.email}>
                    {u.name}
                    {u.email === user?.email && (
                      <span className="text-muted-foreground ml-1 text-xs">(you)</span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              This person owns the meeting in Zoom and receives the calendar invite. The scheduler doesn't need to be on the call.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={today}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="time">Time (ET)</Label>
              <Input
                id="time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
          </div>
          {clientTzLabel ? (
            <div className="flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1.5">
              <Globe className="w-3.5 h-3.5 shrink-0" />
              <span>Client time zone: <strong>{clientTzLabel}</strong></span>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Client time zone not set — add it in the About section of their profile.</p>
          )}

          <div className="space-y-1.5">
            <Label>Duration</Label>
            <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATIONS.map((d) => (
                  <SelectItem key={d.value} value={String(d.value)}>{d.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {emailContacts.length > 0 && (
            <div className="space-y-1.5">
              <Label>Client contacts</Label>
              <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-2">
                {emailContacts.map((c) => (
                  <div key={c.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`contact-${c.id}`}
                      checked={selectedContacts.has(c.id)}
                      onCheckedChange={() => toggleContact(c.id)}
                    />
                    <label htmlFor={`contact-${c.id}`} className="text-sm cursor-pointer">
                      {c.name} <span className="text-muted-foreground">({c.email})</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {staffUsers.length > 0 && (
            <>
              {/* Alternate host — one person who can start/run the meeting as backup */}
              <div className="space-y-1.5">
                <Label>Alternate host <span className="text-muted-foreground font-normal text-xs">— can start/run meeting if host is absent</span></Label>
                <Select value={altHostEmail || "none"} onValueChange={(v) => setAltHostEmail(v === "none" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {staffUsers.filter((u) => u.email !== hostEmail).map((u) => (
                      <SelectItem key={u.id} value={u.email}>
                        {u.name}
                        {u.email === user?.email && (
                          <span className="text-muted-foreground ml-1 text-xs">(you)</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Staff attending — just get the invite, no special Zoom role */}
              <div className="space-y-1.5">
                <Label>Also attending <span className="text-muted-foreground font-normal text-xs">— staff who will join but aren't hosting</span></Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                  <Input
                    placeholder="Search team…"
                    value={staffSearch}
                    onChange={(e) => setStaffSearch(e.target.value)}
                    className="pl-7 h-8 text-sm"
                  />
                </div>
                <div className="space-y-2 max-h-36 overflow-y-auto border rounded-md p-2">
                  {filteredAttendees.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-1">No matches.</p>
                  ) : filteredAttendees.map((u) => (
                    <div key={u.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`attendee-${u.id}`}
                        checked={selectedAttendees.has(u.id)}
                        onCheckedChange={() => toggleAttendee(u.id)}
                      />
                      <label htmlFor={`attendee-${u.id}`} className="text-sm cursor-pointer">
                        {u.name}
                        <span className="text-muted-foreground ml-1">({u.email})</span>
                        {u.calendar_email && (
                          <span className="text-xs text-emerald-600 ml-1">+ {u.calendar_email}</span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label>Add guest</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="guest@example.com"
                value={guestInput}
                onChange={(e) => setGuestInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addGuest(); } }}
                className="flex-1"
              />
              <Button type="button" variant="outline" size="sm" onClick={addGuest} disabled={!guestInput.trim()}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {guestEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {guestEmails.map((email) => (
                  <span key={email} className="flex items-center gap-1 text-xs bg-muted rounded-full px-2 py-1">
                    {email}
                    <button
                      type="button"
                      onClick={() => setGuestEmails((prev) => prev.filter((e) => e !== email))}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            type="button"
            disabled={!topic.trim() || !date || !time || !hostEmail || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Schedule Meeting
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
